import React from "react";
import { useThemeMode } from "../../Context/ThemeContext";
import "../../Styles/ErrorPage.css";

/* --------------------------------------------------------------------------
   ErrorPage — shared full-screen layout for the standalone status pages
   (404, 503). Same split as the 403 page in Components/Restricted: message
   and actions on the left, the illustration on the right.

   code / title / message   the text block
   art                      the illustration, one set per theme:
                            { light: { still, animated }, dark: { still, animated } }
                            The animated one is shown unless the user prefers
                            reduced motion.
   actions                  [{ label, onClick, variant: "primary" | "outline" }]
   -------------------------------------------------------------------------- */
const ErrorPage = ({ code, title, message, art, actions = [] }) => {
    const { resolvedMode } = useThemeMode();
    const { still, animated } = art[resolvedMode] || art.light;

    return (
    <div className="hc-error-page min-vh-100 d-flex align-items-center justify-content-center">
        <div className="container px-md-5">
            <div className="row align-items-center justify-content-center" style={{ minHeight: "80vh" }}>
                {/* LEFT */}
                <div className="col-12 col-lg-6 p-md-5 px-5 order-2 order-lg-1">
                    <h1 className="fw-bold display-2 mb-2" style={{ color: "var(--hc-teal-text)" }}>
                        {code}
                    </h1>
                    <h2 className="fw-semibold fs-2 mb-3" style={{ color: "var(--hc-heading-dark)" }}>
                        {title}
                    </h2>
                    <p className="mb-4 fs-5" style={{ color: "var(--hc-text-muted-slate)" }}>
                        {message}
                    </p>
                    <div className="d-flex flex-column flex-md-row gap-3">
                        {actions.map(({ label, onClick, variant = "primary" }) => (
                            <button
                                key={label}
                                type="button"
                                className={
                                    variant === "primary"
                                        ? "btn w-100 fw-semibold text-light px-4 py-2 rounded-4 border-0 hc-btn-primary-gradient"
                                        : "btn btn-outline-primary px-4 py-2 w-100 rounded-4 fw-semibold"
                                }
                                style={{ fontSize: "1.1rem", borderWidth: variant === "primary" ? undefined : 2 }}
                                onClick={onClick}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
                {/* RIGHT */}
                <div className="col-12 col-lg-6 order-1 order-lg-2 d-flex justify-content-center align-items-center py-4">
                    <picture>
                        {/* The animated picture is skipped for people who ask for reduced motion */}
                        <source srcSet={animated} type="image/webp" media="(prefers-reduced-motion: no-preference)" />
                        <img
                            src={still}
                            alt={`${code} ${title}`}
                            className="w-100"
                            style={{ maxWidth: 980 }}
                        />
                    </picture>
                </div>
            </div>
        </div>
    </div>
    );
};

export default ErrorPage;
